import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, closestCenter, pointerWithin, useSensor, useSensors, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { ArrowLeft, Star, Settings, Plus, Users, Zap } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { toast } from 'sonner';
import { useEzTask } from '../context/EzTaskContext';
import { listApi, cardApi } from '../services/eztaskApi';
import KanbanList from '../components/KanbanList';
import KanbanCard from '../components/KanbanCard';
import CardModal from '../components/CardModal';
import EzTaskDrawer from '../components/EzTaskDrawer';
import CardFilter, { filterCards } from '../components/CardFilter';
import BoardSettings from '../components/BoardSettings';

// Custom collision detection that works well for Kanban boards
const customCollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return closestCenter(args);
};

export default function BoardView() {const { t } = useTranslation("eztask");
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { currentBoard, loading, fetchBoard, updateBoard, refreshBoard } = useEzTask();
  const [activeCard, setActiveCard] = useState(null);
  const [activeList, setActiveList] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showAddList, setShowAddList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [filters, setFilters] = useState({
    memberIds: [],
    labelIds: [],
    dueDate: null,
    priority: null
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    if (boardId) {
      fetchBoard(boardId);
    }
  }, [boardId, fetchBoard]);

  const handleDragStart = (event) => {
    const { active } = event;
    if (active.data.current?.type === 'card') {
      setActiveCard(active.data.current.card);
    } else if (active.data.current?.type === 'list') {
      setActiveList(active.data.current.list);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) {
      setActiveCard(null);
      setActiveList(null);
      return;
    }

    const activeType = active.data.current?.type;
    const overType = over.data.current?.type;

    try {
      if (activeType === 'card') {
        const cardId = active.data.current.card.card_id;
        let targetListId = over.data.current?.list?.list_id;

        // If dropped on another card, get the parent list
        if (overType === 'card') {
          targetListId = over.data.current.card.list_id || over.data.current.listId;
        }

        if (targetListId) {
          // Calculate position
          const overListCards = currentBoard.lists.find((l) => l.list_id === targetListId)?.cards || [];
          let position = 0;
          if (overType === 'card') {
            const overCardIndex = overListCards.findIndex((c) => c.card_id === over.data.current.card.card_id);
            position = overCardIndex >= 0 ? overCardIndex : overListCards.length;
          } else {
            position = overListCards.length;
          }

          await cardApi.move(cardId, targetListId, position);
          await refreshBoard();
        }
      } else if (activeType === 'list') {
        const lists = currentBoard.lists;
        const oldIndex = lists.findIndex((l) => l.list_id === active.id);
        const newIndex = lists.findIndex((l) => l.list_id === over.id);

        if (oldIndex !== newIndex) {
          const reordered = arrayMove(lists, oldIndex, newIndex);
          const items = reordered.map((l, i) => ({ id: l.list_id, position: i }));
          await listApi.reorder(boardId, items);
          await refreshBoard();
        }
      }
    } catch {
      toast.error('Failed to move item');
    }

    setActiveCard(null);
    setActiveList(null);
  };

  const handleToggleStar = async () => {
    try {
      await updateBoard(boardId, { is_starred: !currentBoard.is_starred });
      toast.success(currentBoard.is_starred ? 'Removed from starred' : 'Added to starred');
    } catch {
      toast.error('Failed to update board');
    }
  };

  const handleAddList = async () => {
    if (!newListName.trim()) return;
    try {
      await listApi.create(boardId, { name: newListName });
      setNewListName('');
      setShowAddList(false);
      await refreshBoard();
      toast.success('List created');
    } catch {
      toast.error('Failed to create list');
    }
  };

  const handleCardClick = (card) => {
    setSelectedCard(card);
  };

  if (loading || !currentBoard) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>);

  }

  const getBackgroundUrl = (path) => {
    if (!path) return undefined;
    if (path.startsWith('http')) return `url(${path})`;
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    return `url(${API_BASE}/${normalizedPath})`;
  };

  const boardStyle = {
    backgroundColor: currentBoard.background_color || '#0079BF',
    backgroundImage: getBackgroundUrl(currentBoard.background_image),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed'
  };

  return (
    <div className="min-h-screen flex flex-col" style={boardStyle} data-testid="eztask-board-view">
      {/* Board Header */}
      <div className="bg-black/30 backdrop-blur-sm px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/eztask')} className="text-white hover:bg-white/20">
              <ArrowLeft className="w-4 h-4 mr-2" />{t("boards", "Boards")}

            </Button>
            <h1 className="text-xl font-bold text-white">{currentBoard.name}</h1>
            <button
              onClick={handleToggleStar}
              className="text-white hover:text-yellow-400 transition-colors"
              data-testid="star-board-btn">
              
              <Star className={`w-5 h-5 ${currentBoard.is_starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {/* Card Filter */}
            <CardFilter
              members={currentBoard.members || []}
              labels={currentBoard.labels || []}
              filters={filters}
              onFilterChange={setFilters} />
            
            <div className="flex -space-x-2">
              {currentBoard.members?.slice(0, 5).map((member) =>
              <div
                key={member.id}
                className="w-8 h-8 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-white text-xs font-semibold"
                title={member.user_name}>
                
                  {member.user_name?.charAt(0).toUpperCase() || '?'}
                </div>
              )}
              {currentBoard.members?.length > 5 &&
              <div className="w-8 h-8 rounded-full bg-slate-600 border-2 border-white flex items-center justify-center text-white text-xs">
                  +{currentBoard.members.length - 5}
                </div>
              }
            </div>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/20">
              <Users className="w-4 h-4 mr-2" />{t("members", "Members")}

            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={() => navigate(`/eztask/board/${boardId}/automations`)}
              data-testid="automation-settings-btn">
              
              <Zap className="w-4 h-4 mr-2" />{t("automations", "Automations")}

            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
              onClick={() => setShowSettings(true)}
              data-testid="board-settings-btn">
              
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Board Content */}
      <div className="flex-1 overflow-x-auto p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={customCollisionDetection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}>
          
          <div className="flex gap-4 items-start h-full">
            <SortableContext items={currentBoard.lists?.map((l) => l.list_id) || []} strategy={horizontalListSortingStrategy}>
              {currentBoard.lists?.map((list) => {
                const hasActiveFilters = filters.memberIds?.length || filters.labelIds?.length || filters.dueDate || filters.priority;
                const filteredListCards = hasActiveFilters ? filterCards(list.cards, filters) : undefined;
                return (
                  <KanbanList
                    key={list.list_id}
                    list={list}
                    onCardClick={handleCardClick}
                    onRefresh={refreshBoard}
                    filteredCards={filteredListCards} />);


              })}
            </SortableContext>

            {/* Add List */}
            {showAddList ?
            <div className="w-72 flex-shrink-0 bg-slate-800 rounded-lg p-3">
                <Input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="Enter list title..."
                className="mb-2 bg-slate-900 border-slate-600"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleAddList()}
                data-testid="new-list-input" />
              
                <div className="flex gap-2">
                  <Button onClick={handleAddList} size="sm" data-testid="add-list-confirm">{t("addList", "Add List")}</Button>
                  <Button variant="ghost" size="sm" onClick={() => {setShowAddList(false);setNewListName('');}}>{t("cancel", "Cancel")}</Button>
                </div>
              </div> :

            <Button
              variant="ghost"
              className="w-72 flex-shrink-0 justify-start bg-white/20 hover:bg-white/30 text-white"
              onClick={() => setShowAddList(true)}
              data-testid="add-list-btn">
              
                <Plus className="w-4 h-4 mr-2" />{t("addAnotherList", "Add another list")}

            </Button>
            }
          </div>

          <DragOverlay>
            {activeCard &&
            <div className="opacity-80 transform rotate-3">
                <KanbanCard card={activeCard} isDragging />
              </div>
            }
          </DragOverlay>
        </DndContext>
      </div>

      {/* Card Modal / Drawer */}
      {selectedCard && !selectedCard.is_agent_task &&
      <CardModal
        cardId={selectedCard.card_id}
        boardLabels={currentBoard.labels}
        boardMembers={currentBoard.members}
        onClose={() => setSelectedCard(null)}
        onUpdate={refreshBoard} />

      }

      {selectedCard && selectedCard.is_agent_task &&
      <EzTaskDrawer
        cardId={selectedCard.card_id}
        isOpen={!!selectedCard}
        onClose={() => setSelectedCard(null)}
        onUpdate={refreshBoard} />

      }

      {showSettings &&
      <BoardSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        board={currentBoard} />

      }
    </div>);

}